import React, { PureComponent } from 'react';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { MdReportProblem } from 'react-icons/md';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { firebaseConnect, isEmpty, isLoaded } from 'react-redux-firebase';
import dayjs from 'dayjs';
import { compose } from 'redux';
import styled from 'styled-components';

import IncrementSelector from '../../components/increment-selector';
import Indicator from '../../components/indicator';
import Timeslot from '../../components/timeslot';
import { TIMESLOT_SELECT_CUT_OFF_TIME } from '../../constants';
import SwipeableContainer from '../calendar/swipeable-container';
import { STORE_TIMESLOT_AS } from './constants';
import { getFilteredTimeslots, getStartOfDay } from './selectors';

/**
 * Timeslot container
 */
const TimeslotContainer = styled.div`
  display: grid;
  grid-template-rows: auto;
  justify-content: center;
  grid-column-gap: 0.8em;
  grid-row-gap: 0.8em;
  padding: 0 0.4em 0 0.4em;
  margin: 0 0.4em 0 0.4em;
  font-weight: bold;
  grid-template-columns: 1fr 1fr;
  overflow-y: auto;
`;

const UnderLinedText = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

/**
 * Booking form component
 * Houses all logic for booking games and is redux aware
 */
class Schedule extends PureComponent {
  /**
   * Callback for day selector
   * @param {number} diff The requested change to month, whether it be to plus or minus a day
   */
  onDayChange = diff => {
    const newDate = dayjs(this.props.day).add(diff, 'day');
    this.props.onTimeChange('schedule', newDate);
  };
  onMonthClick = () => {
    const month = dayjs(this.props.day).startOf('month');
    this.props.onTimeChange('calendar', month);
  };
  /**
   * Called when timeslot is clicked
   * Dispatches redux timeslot action and changes page
   */
  onTimeslotClick = timeslotId => {
    const timeslot = this.props.timeslots.find(
      timeslot => timeslot.key === timeslotId
    );
    this.props.onTimeslotSelect(timeslot);
  };

  /**
   * Renders all timeslots for this day
   */
  renderTimeslots = () => {
    if (!isLoaded(this.props.timeslots)) {
      return (
        <Indicator
          spin
          style={{
            gridColumn: '1/3',
            height: '22em'
          }}
        />
      );
    }
    if (isEmpty(this.props.timeslots)) {
      return (
        <Translate>
          {({ translate }) => (
            <Indicator
              icon={MdReportProblem}
              style={{
                gridColumn: '1/3',
                height: '22em'
              }}
              text={translate('schedule.noTimeslots')}
            />
          )}
        </Translate>
      );
    }
    return this.props.timeslots.map(timeslot => {
      const { key, value } = timeslot;
      return (
        <Timeslot
          cancelled={value.cancelled}
          date={dayjs.unix(value.time)}
          isHoliday={value.isHoliday}
          key={key}
          onClick={this.onTimeslotClick}
          playerCapacity={+value.playerCapacity}
          playerCount={+value.playerCount}
          value={key}
        />
      );
    });
  };

  /**
   * @inheritDoc
   */
  render() {
    const { day, style, onClick } = this.props;
    return (
      <SwipeableContainer
        className="swipe"
        onClick={onClick}
        onSwipedLeft={() => this.onDayChange(1)}
        onSwipedRight={() => this.onDayChange(-1)}
        style={style}
        trackMouse
      >
        <IncrementSelector onClick={this.onDayChange}>
          <UnderLinedText onClick={this.onMonthClick}>
            <FaRegCalendarAlt size="0.8em" style={{ marginRight: '0.2em' }} />
            {day.format('M月')}
          </UnderLinedText>
          {day.format('D日 (ddd)')}
        </IncrementSelector>
        <TimeslotContainer>{this.renderTimeslots()}</TimeslotContainer>
      </SwipeableContainer>
    );
  }
}

/**
 * Map Redux State to props
 */
const mapStateToProps = (state, props) => ({
  gameId: state.global.gameId,
  day: getStartOfDay(state, props),
  timeslots: getFilteredTimeslots(state, props)
});

export default compose(
  connect(mapStateToProps, null),
  firebaseConnect(({ day, gameId }) => {
    const startOfBookingWindow = dayjs().subtract(
      TIMESLOT_SELECT_CUT_OFF_TIME,
      'minute'
    );
    const startTime = day.isBefore(startOfBookingWindow)
      ? startOfBookingWindow
      : day;
    const endOfDay = dayjs(day).endOf('day');
    return [
      {
        path: `timeslots/${gameId}`,
        queryParams: [
          'orderByChild=time',
          'startAt=' + startTime.unix(),
          'endAt=' + endOfDay.unix()
        ],
        storeAs: STORE_TIMESLOT_AS
      },
      {
        path: `/games/${gameId}`
      }
    ];
  })
)(Schedule);
